import {useState, useCallback, useEffect} from 'react'
import {
  UserSectionTitleButton,
  Separator,
  UserSectionTitle,
  AddUserButton,
  FlexAlignCenterBox,
  StyledToggleButtonGroup,
  FlexSpaceBetweenBox,
  FlexGapBox,
  StyledAddCircleIcon,
  VerticalMarginBox
} from './UserManagementStyle'
import UserDetailListItems from './UserDetailListItems'
import {useGetUserListDetails} from '../api/getUserDetails'
import {useParams} from 'react-router-dom'
import SearchPanel from '../common/SearchPanel'
import LoadingIcon from '../common/LoadingIcon/LoadingIcon'
import CreateUserComponent from '../UserManagement/CreateUserComponent'
import {useRemoveUser} from '../api/removeUser'
import {useEditUser} from '../api/editUser'
import InfoNotificationComponent from '../ProjectDetails/InfoNotification/InfoNotificationComponent'
import {AddButton, NavigationBox, NavigationItem} from '../ProjectDetails/ProjectDetailStyle'
import {useProjectDetails} from '../../store/hooks/projectDetails'
import loadingTitles from '../../appConfig/loadingTitles.config.json'
import {SortComponent} from '../common/SortComponent'
import PropTypes from 'prop-types'
import {useGetGroupPermissionDetails} from '../api/getGroupProjectList'
import {formatDate} from './utils/dateFormatter'
import {StyledUserTablePagination} from '../AdminPanelStyling'
import TablePaginationActions from '../common/TablePaginationActions'

export const createVariables = (firstName, lastName, userEmail, rowId, startDate, endDate, isTemporary) => {
  const variables = {}
  if (firstName) variables.firstName = firstName.trim()
  if (lastName) variables.lastName = lastName.trim()
  if (userEmail) variables.emailId = userEmail
  if (rowId) variables.rowId = rowId
  if (startDate) variables.startDate = startDate
  if (endDate) variables.endDate = endDate
  if (isTemporary !== undefined) variables.isTemporary = isTemporary
  return variables
}
const UserManagementComponent = ({
  isCompleted,
  setRenderUserView,
  clientProjectId,
  handleAddAdmin,
  addButtonDisableConditions,
  setInputValue,
  isClientComponent,
  clientUserData
}) => {
  const {hasUsers} = useProjectDetails()
  const [userAvailable, setUserAvailable] = useState(hasUsers)
  const {projectId} = useParams()
  const [viewArchive, setViewArchive] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [getUserListData, {userData, loadingUserList, error: errorGraphql, refetch}] = useGetUserListDetails(
    clientProjectId || projectId,
    viewArchive
  )
  const [emailData, setEmailData] = useState()
  const searchKey = ['FULL_NAME', 'FIRST_NAME', 'LAST_NAME', 'EMAIL_ID']
  const [tableData, setTableData] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [userAdded, setUserAdded] = useState()
  const [removeUserFromGroup, {data: deletedUser, loading: deletingUser, error: errorDeletingUser}] = useRemoveUser()
  const [editUserDetails, {data: editedUser, loading: editingUser, error: errorEditingUser}] = useEditUser()

  const [showNotification, setShowNotification] = useState(false)
  const [showNotificationForEdit, setShowNotificationForEdit] = useState(false)
  const [view, setView] = useState('all')
  const [openCreateUser, setOpenCreateUser] = useState(false)
  const {clientId} = useParams()
  const {groupPermissionListDetails} = useGetGroupPermissionDetails(projectId, clientId)
  const selectedJurisdictionIndex = groupPermissionListDetails?.groupPermissionList?.[0]?.JURISDICTION
  const selectedFilterKeys = groupPermissionListDetails?.groupPermissionList?.[0]?.FILTERS

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage)
  }, [])

  const handleRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(+e.target.value)
    setPage(0)
  }, [])

  const userListWithFullname = () => {
    const modifiedData = userData?.userList[0]?.USER_LIST?.map((item) => ({
      ...item,
      FULL_NAME: item.FIRST_NAME + ' ' + item.LAST_NAME
    }))
    return modifiedData
  }

  const modifyUserDataWithFormattedDates = (userData) => {
    const formattedData = userData?.userList?.[0]?.USER_LIST?.map((user) => ({
      ...user,
      START_DATE: formatDate(user.START_DATE),
      END_DATE: formatDate(user.END_DATE)
    }))
    return {...userData, userList: [{USER_LIST: formattedData}]}
  }

  useEffect(() => {
    if (!clientProjectId && projectId) {
      getUserListData({
        variables: {groupId: Number(projectId), archive: viewArchive, page: page + 1, pageSize: rowsPerPage}
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, viewArchive, getUserListData, page, rowsPerPage])

  useEffect(() => {
    if (clientProjectId) {
      getUserListData({
        variables: {groupId: Number(clientProjectId), archive: viewArchive, page: page + 1, pageSize: rowsPerPage}
      })
    }
  }, [clientProjectId, viewArchive, getUserListData, page, rowsPerPage])

  useEffect(() => {
    if (userData?.userList && !loadingUserList && userData?.userList?.length >= 0 && !errorGraphql && isCompleted) {
      const modifiedData = modifyUserDataWithFormattedDates(userData)
      setTableData(modifiedData?.userList?.[0]?.USER_LIST || [])
      !viewArchive && setUserAvailable((modifiedData?.userList?.[0]?.USER_LIST?.length || 0) > 0)
      !viewArchive && setEmailData(userData?.userList?.[0]?.USER_LIST?.map((element) => element.EMAIL_ID) || [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData])

  useEffect(() => {
    const searchString = searchTerm.toLowerCase().trim()
    if (userData) {
      const filteredList = userListWithFullname()?.filter((item) =>
        searchKey.some((key) => item[key].toLowerCase().includes(searchString))
      )
      setTableData(filteredList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  useEffect(() => {
    if (userAdded) {
      setUserAvailable(true)
      refetch()
      setShowNotification(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAdded])

  useEffect(() => {
    if (!deletingUser && errorDeletingUser) {
      setShowNotification(true)
      setUserAdded(false)
    }
  }, [deletingUser, errorDeletingUser])

  useEffect(() => {
    if (!editingUser && errorEditingUser) {
      setShowNotificationForEdit(true)
    }
  }, [editingUser, errorEditingUser])

  useEffect(() => {
    if (!deletingUser && deletedUser) {
      refetch()
      setShowNotification(true)
      setUserAvailable(true)
      setUserAdded(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletingUser, deletedUser])

  useEffect(() => {
    if (!editingUser && editedUser) {
      refetch()
      setShowNotificationForEdit(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingUser, editedUser])

  const handleDeleteUser = (userEmail) => {
    removeUserFromGroup({
      variables: {
        groupId: projectId || clientProjectId,
        email: userEmail,
        jurisdiction: selectedJurisdictionIndex,
        filters: selectedFilterKeys
      }
    })
  }

  const handleEditUser = (firstName, lastName, userEmail, startDate, endDate, isTemporary, rowId) => {
    const variables = createVariables(firstName, lastName, userEmail, rowId, startDate, endDate, isTemporary)

    // Call editUserDetails with the constructed variables
    editUserDetails({
      variables
    })
  }

  const handleViewChange = (event, nextView) => {
    if (nextView !== null && nextView !== view) {
      setView(nextView)
      setViewArchive(nextView === 'archive')
      setPage(0)
      setRowsPerPage(5)
      refetch()
      const modifiedData = modifyUserDataWithFormattedDates(userData)
      setTableData(modifiedData)
    }
  }

  const title = clientProjectId ? 'All Admins in this client group' : 'All users in this project'
  return (
    <div>
      <NavigationBox>
        <NavigationItem selected={false} onClick={() => setRenderUserView(false)}>
          Project permissions
        </NavigationItem>
        <NavigationItem selected={true}>Users of this project</NavigationItem>
      </NavigationBox>

      {!isCompleted && <InfoNotificationComponent fromUserManagement={true} setRenderUserView={setRenderUserView} />}
      <CreateUserComponent
        isUserManagement={!isClientComponent}
        userData={emailData}
        setUserAdded={setUserAdded}
        isCompleted={isCompleted}
        showSectionDivider={userAvailable || hasUsers || showNotification}
        setInputValue={setInputValue}
        isClientComponent={isClientComponent}
        clientUserData={clientUserData}
        open={openCreateUser}
        setOpen={setOpenCreateUser}
      />
      <div>
        {clientProjectId && (
          <AddButton aria-label='add-user' onClick={handleAddAdmin} disabled={addButtonDisableConditions()}>
            Add
          </AddButton>
        )}
      </div>
      {isCompleted &&
        (hasUsers ||
          userAvailable ||
          showNotification ||
          clientProjectId ||
          (!loadingUserList && userData?.userList)) && (
          <FlexAlignCenterBox>
            {!isClientComponent && (
              <StyledToggleButtonGroup
                color='primary'
                value={view}
                exclusive
                onChange={(event, newValue) => handleViewChange(event, newValue)}
                sx={{height: '40px'}}
              >
                <UserSectionTitleButton
                  value='all'
                  isCompleted={isCompleted}
                  tabIndex={0}
                  aria-label='All Users in this Project'
                  selected={view === 'all'}
                >
                  {title}
                </UserSectionTitleButton>
                <Separator>|</Separator>
                <UserSectionTitleButton
                  value='archive'
                  isCompleted={isCompleted}
                  tabIndex={0}
                  aria-label='View Archive'
                  selected={view === 'archive'}
                >
                  View Archive
                </UserSectionTitleButton>
              </StyledToggleButtonGroup>
            )}
            {isClientComponent && (
              <UserSectionTitle isCompleted={isCompleted} tabIndex={0} aria-label={title}>
                {title}
              </UserSectionTitle>
            )}
          </FlexAlignCenterBox>
        )}

      {(loadingUserList || deletingUser || editingUser) && isCompleted && (
        <LoadingIcon data-testid='loading-icon' title={loadingTitles.auditList} />
      )}
      {((!loadingUserList && !deletingUser && !editingUser && isCompleted && userData?.userList) || errorGraphql) && (
        <div>
          {((userData?.userList?.length !== 0 && userData?.userList?.[0]?.USER_LIST?.length !== 0 && !errorGraphql) ||
            showNotification) && (
            <FlexSpaceBetweenBox>
              <FlexGapBox>
                <SearchPanel
                  aria-label='search-for-a-user'
                  data-testid='search-panel'
                  searchTerm={searchTerm}
                  updateSearchTerm={setSearchTerm}
                />
                {!isClientComponent && (
                  <AddUserButton
                    variant='outlined'
                    aria-label='Add User'
                    onClick={() => setOpenCreateUser(true)}
                    disabled={!isCompleted}
                  >
                    <StyledAddCircleIcon />
                    Add New User
                  </AddUserButton>
                )}
              </FlexGapBox>
              <SortComponent
                setSortedData={setTableData}
                data={tableData}
                view={view}
                isClientComponent={isClientComponent}
              />
            </FlexSpaceBetweenBox>
          )}
          {!isClientComponent && tableData.length === 0 && !loadingUserList && (
            <VerticalMarginBox>
              <AddUserButton
                variant='outlined'
                aria-label='Add User'
                onClick={() => setOpenCreateUser(true)}
                disabled={!isCompleted}
              >
                <StyledAddCircleIcon />
                Add New User
              </AddUserButton>
            </VerticalMarginBox>
          )}
          <UserDetailListItems
            data={tableData}
            loading={loadingUserList || deletingUser || editingUser}
            handleDeleteUser={handleDeleteUser}
            handleEditUser={handleEditUser}
            showNotification={showNotification}
            showNotificationForEdit={showNotificationForEdit}
            setShowNotification={setShowNotification}
            setShowNotificationForEdit={setShowNotificationForEdit}
            error={errorGraphql}
            errorDeletingUser={errorDeletingUser}
            errorEditingUser={errorEditingUser}
            refetch={refetch}
            userAdded={userAdded}
            deletedUser={deletedUser}
            isClientComponent={isClientComponent}
            view={view}
          />
          {(userData?.userList?.[0]?.TOTAL_COUNT || 0) > 0 && !searchTerm && (
            <StyledUserTablePagination
              component='div'
              rowsPerPageOptions={[5, 10, 25, 50, 75, 100]}
              count={userData?.userList?.[0]?.TOTAL_COUNT || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage='Rows per page:'
            />
          )}
        </div>
      )}
    </div>
  )
}

UserManagementComponent.propTypes = {
  isCompleted: PropTypes.bool.isRequired,
  setRenderUserView: PropTypes.func.isRequired,
  clientProjectId: PropTypes.string,
  handleAddAdmin: PropTypes.func,
  addButtonDisableConditions: PropTypes.func,
  setInputValue: PropTypes.func,
  isClientComponent: PropTypes.bool,
  clientUserData: PropTypes.object
}

export default UserManagementComponent
